import type { ActionExecutor } from "../TriggerEngine.interfaces";
import PlaylistEventsManager from "../Devices/PlaylistEventsManager";
import { PlaylistEvents } from "../Devices/PlaylistEventsManager.interface";

/**
 * `PlaylistEvents.Play` es lo que pone la pantalla en "reproduciendo", así que
 * solo se emite cuando hay audio de verdad: con la lista vacía o entera
 * irreproducible `play(0)` devuelve `false` —y ya cerró la rotación con
 * `PlaylistEmpty`— y emitir igual volvía a prender el transporte encima de esa
 * limpieza. El reanudar sigue anunciándose como siempre, cuando reanuda.
 */
export const playlistPlay: ActionExecutor = async (radio) => {
  const paused = radio.Playlist.isPaused();

  if (paused === true) {
    // Este `await` puede durar TODA una bajada: cuando la pista se pausó
    // mientras se bajaba, el reanudar se cuelga del arranque en vuelo. Es la
    // misma espera que ya tienen las otras ramas de esta acción y
    // `playlist_next`/`playlist_prev` —todas terminan en `PlaylistTrack.play()`
    // y su fetch—, y `TriggerEngine` mantiene `executing` tomado mientras tanto,
    // así que los eventos que lleguen en esa ventana se descartan.
    //
    // NO convertir esto en un `void resume().then(...)` para soltar el candado:
    // ese candado es lo único que hoy impide que un SEGUNDO `playlist_play` de
    // la misma ventana caiga en la rama de abajo —el reanudar ya levantó el
    // latch, así que `isPaused()` contesta `undefined`— y arranque
    // `play(0)`, que destruye la pista en vuelo, aborta la bajada y reinicia la
    // lista desde la primera. El aire muerto se acota bajando la pista, no
    // soltando el candado.

    if (await radio.Playlist.resume()) {
      PlaylistEventsManager.emit(PlaylistEvents.Play);
    }

    return;
  }

  if (paused === undefined) {
    await radio.Playlist.update();

    if (await radio.Playlist.play(0)) {
      PlaylistEventsManager.emit(PlaylistEvents.Play);
    }

    return;
  }

  // `false` = ya está sonando: el disparador no cambia nada, pero la señal
  // para la UI se mantiene.
  PlaylistEventsManager.emit(PlaylistEvents.Play);
};

export const playlistPause: ActionExecutor = (radio) => {
  radio.Playlist.pause();
  PlaylistEventsManager.emit(PlaylistEvents.Pause);
};

export const playlistNext: ActionExecutor = async (radio) => {
  await radio.Playlist.nextTrack();
};

export const playlistPrev: ActionExecutor = async (radio) => {
  await radio.Playlist.prevTrack();
};
